import { FunctionComponent, useEffect, useState } from 'react'
import { Box, Button, MenuItem, CircularProgress } from '@material-ui/core'
import { makeStyles } from '@material-ui/styles'
import { TextField } from 'formik-material-ui'
import { Formik, Form, Field } from 'formik'
import * as Yup from 'yup'
import { AlertTriangle, Clock, Check } from 'react-feather'
import { toast } from 'react-toastify'
import axios from '../../utils/axios'
import colors from '../../colors'
import { SPACING } from '../../constants'

/**
 * SMS setup: collecting the business details Twilio needs to register the
 * brewery's A2P brand.
 *
 * Nothing here is pre-filled from Stripe Connect. Standard Connect accounts
 * give the platform only restricted access, and where details are readable, a
 * mismatch between what Stripe holds and what Twilio is told is exactly what
 * gets a brand rejected — days later and with no obvious cause.
 */

type Status = 'collecting' | 'submitted' | 'in_review' | 'approved' | 'rejected'

interface Registration {
  status: Status
  /** A mock registration reaches approved and still cannot send. */
  mock: boolean
  rejectionReason: string | null
  readyToSubmit: boolean
  /** Field names, snake_case — they come from the server untouched, since the
   *  case converter rewrites keys but not values. */
  missingDetails: string[]
  submittedAt: string | null
  approvedAt: string | null
  details: Record<string, string | null>
}

interface SetupResponse {
  eligible: boolean
  alreadySending: boolean
  registration: Registration | null
}

const BUSINESS_TYPES = [
  { value: 'llc', label: 'LLC' },
  { value: 'corporation', label: 'Corporation' },
  { value: 'partnership', label: 'Partnership' },
  { value: 'non_profit', label: 'Non-profit' },
]

const INDUSTRIES = [
  { value: 'food_and_beverage', label: 'Food & beverage' },
  { value: 'hospitality', label: 'Hospitality' },
  { value: 'retail', label: 'Retail' },
  { value: 'entertainment', label: 'Entertainment' },
]

// Twilio's accepted job_position values. There is no "owner" level - a
// brewery owner picks CEO. Offering a friendlier label that Twilio rejects
// in review is worse than a slightly corporate one that passes.
const JOB_POSITIONS = [
  { value: 'ceo', label: 'CEO / Owner' },
  { value: 'cfo', label: 'CFO' },
  { value: 'director', label: 'Director' },
  { value: 'gm', label: 'General Manager' },
  { value: 'vp', label: 'VP' },
  { value: 'general_counsel', label: 'General Counsel' },
  { value: 'other', label: 'Other' },
]

interface FieldSpec {
  name: string
  label: string
  helperText?: string
  options?: { value: string, label: string }[]
  /** Narrow fields sit two to a row. */
  half?: boolean
}

const SECTIONS: { title: string, caption?: string, fields: FieldSpec[] }[] = [
  {
    title: 'Your business',
    caption: 'This must match your official registration exactly. Carriers check it.',
    fields: [
      { name: 'legal_business_name', label: 'Legal business name', helperText: 'As registered, including LLC or Inc.' },
      { name: 'business_type', label: 'Business type', options: BUSINESS_TYPES, half: true },
      { name: 'business_industry', label: 'Industry', options: INDUSTRIES, half: true },
      { name: 'business_registration_number', label: 'EIN', helperText: '9 digits, e.g. 12-3456789', half: true },
      { name: 'website_url', label: 'Website', helperText: 'Include https://', half: true },
    ],
  },
  {
    title: 'Business address',
    fields: [
      { name: 'address_street', label: 'Street address' },
      { name: 'address_city', label: 'City', half: true },
      { name: 'address_region', label: 'State', helperText: 'Two letters, e.g. ID', half: true },
      { name: 'address_postal_code', label: 'ZIP code', half: true },
    ],
  },
  {
    title: 'Authorized representative',
    caption: 'Someone carriers can contact about this registration.',
    fields: [
      { name: 'rep_first_name', label: 'First name', half: true },
      { name: 'rep_last_name', label: 'Last name', half: true },
      { name: 'rep_email', label: 'Email', half: true },
      { name: 'rep_phone', label: 'Phone', helperText: 'e.g. +12085550100', half: true },
      { name: 'rep_business_title', label: 'Job title', helperText: 'e.g. Owner, Taproom Manager', half: true },
      { name: 'rep_job_position', label: 'Role', helperText: 'Carriers require one of these levels', options: JOB_POSITIONS, half: true },
    ],
  },
]

const ALL_FIELDS = SECTIONS.flatMap((section) => section.fields)

const validationSchema = Yup.object({
  business_registration_number: Yup.string()
    .matches(/^\d{2}-?\d{7}$/, 'Must be a 9-digit EIN')
    .nullable(),
  website_url: Yup.string().url('Must be a full URL, including https://').nullable(),
  rep_email: Yup.string().email('Must be a valid email').nullable(),
  address_postal_code: Yup.string()
    .matches(/^\d{5}(-\d{4})?$/, 'Must be a valid ZIP code')
    .nullable(),
  address_region: Yup.string().length(2, 'Use the two-letter state code').nullable(),
})

// Keys arrive camelCased by the axios case converter, but Formik and the
// server's own missing_details both speak snake_case, so they are converted
// back on load rather than maintaining two names per field.
const toSnake = (value: string) => value.replace(/[A-Z]/g, (c) => `_${c.toLowerCase()}`)

const initialValuesFrom = (registration: Registration | null) => {
  const details = registration?.details ?? {}
  const bySnakeKey: Record<string, string> = {}
  Object.entries(details).forEach(([key, value]) => {
    bySnakeKey[toSnake(key)] = value ?? ''
  })
  return ALL_FIELDS.reduce<Record<string, string>>((acc, field) => {
    acc[field.name] = bySnakeKey[field.name] ?? ''
    return acc
  }, {})
}

const SmsSetup: FunctionComponent = () => {
  const styles = useStyles()
  const [setup, setSetup] = useState<SetupResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)

  const load = async () => {
    try {
      const res = await axios.get<SetupResponse>('/twilio_registration')
      setSetup(res.data)
    } catch {
      toast.error('Could not load SMS setup')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { load() }, [])

  if (loading) {
    return <Box className={styles.loading}><CircularProgress size={24} /></Box>
  }

  if (!setup) return null

  if (!setup.eligible) {
    return (
      <Box className={styles.container}>
        <h2 className={styles.title}>SMS marketing</h2>
        <Box className={styles.notice}>
          <p className={styles.noticeBody}>
            SMS marketing is part of the Pro plan. Upgrade to text your members
            directly.
          </p>
        </Box>
      </Box>
    )
  }

  if (setup.alreadySending) {
    return (
      <Box className={styles.container}>
        <h2 className={styles.title}>SMS marketing</h2>
        <Box className={`${styles.notice} ${styles.noticeDone}`}>
          <Check size={16} />
          <p className={styles.noticeBody}>
            Your texting number is already set up. Nothing to do here.
          </p>
        </Box>
      </Box>
    )
  }

  const registration = setup.registration
  const status = registration?.status ?? 'collecting'
  // Under review means Twilio is vetting these exact values, so they must not
  // move underneath the review. A rejection is editable again — correcting and
  // resubmitting is the point.
  const underReview = status === 'submitted' || status === 'in_review'
  const approved = status === 'approved'
  const editable = !underReview && !approved

  // Taken from the server rather than re-derived here, so the form and the
  // API cannot disagree about what Twilio needs.
  const outstanding = new Set(registration?.missingDetails ?? [])

  const handleRegister = async () => {
    setSubmitting(true)
    try {
      const res = await axios.post<SetupResponse>('/twilio_registration/submit')
      setSetup(res.data)
      toast.success('Sent to the carriers for review')
    } catch (error: any) {
      const data = error?.response?.data
      // A 502 carries the recorded state back, so a rejection shows on the
      // page rather than only in a toast that disappears.
      if (data?.registration) {
        setSetup({ ...setup, registration: data.registration })
      }
      toast.error(data?.error ?? 'Could not submit your registration')
    } finally {
      setSubmitting(false)
    }
  }

  const handleSubmit = async (values: Record<string, string>) => {
    try {
      const res = await axios.put<SetupResponse>('/twilio_registration', {
        twilio_registration: values,
      })
      setSetup(res.data)
      toast.success('Details saved')
    } catch (error: any) {
      const errors = error?.response?.data?.errors
      if (errors) {
        // Server-side validation is the authority; Yup only catches the
        // obvious cases earlier.
        Object.entries(errors).forEach(([field, messages]) => {
          toast.error(`${field.replace(/_/g, ' ')}: ${(messages as string[]).join(', ')}`)
        })
      } else {
        toast.error(error?.response?.data?.error ?? 'Could not save details')
      }
    }
  }

  return (
    <Box className={styles.container}>
      <h2 className={styles.title}>SMS marketing</h2>
      <p className={styles.intro}>
        Before you can text your members, carriers require your business to be
        registered. We handle the filing — we just need your details. Approval
        usually takes a few business days.
      </p>

      {approved && !registration?.mock && (
        <Box className={`${styles.notice} ${styles.noticeDone}`}>
          <Check size={16} />
          <p className={styles.noticeBody}>
            You're approved and ready to text your members.
          </p>
        </Box>
      )}

      {/* A mock brand is approved by Twilio and still cannot send, and it
          expires after 30 days. Saying "ready to text" here contradicted
          Notify, which correctly refuses the send. */}
      {approved && registration?.mock && (
        <Box className={`${styles.notice} ${styles.noticeWaiting}`}>
          <AlertTriangle size={16} />
          <Box>
            <p className={styles.noticeTitle}>Test registration</p>
            <p className={styles.noticeBody}>
              This registration went through Twilio's test path, so it is
              approved but cannot send real texts and expires after 30 days.
              A real registration is needed before texting members.
            </p>
          </Box>
        </Box>
      )}

      {underReview && (
        <Box className={`${styles.notice} ${styles.noticeWaiting}`}>
          <Clock size={16} />
          <p className={styles.noticeBody}>
            Your registration is with the carriers. This usually takes a few
            business days — we'll switch SMS on automatically once it clears.
            Details can't be changed while it's under review.
          </p>
        </Box>
      )}

      {status === 'rejected' && (
        <Box className={`${styles.notice} ${styles.noticeError}`}>
          <AlertTriangle size={16} />
          <Box>
            <p className={styles.noticeTitle}>Registration was rejected</p>
            <p className={styles.noticeBody}>
              {registration?.rejectionReason}
            </p>
            <p className={styles.noticeBody}>
              Correct the details below and save to try again.
            </p>
          </Box>
        </Box>
      )}

      <Formik
        initialValues={initialValuesFrom(registration)}
        validationSchema={validationSchema}
        enableReinitialize
        onSubmit={handleSubmit}
      >
        {({ isSubmitting, dirty }) => (
          <Form>
            {SECTIONS.map((section) => (
              <Box key={section.title} className={styles.section}>
                <h3 className={styles.sectionTitle}>{section.title}</h3>
                {section.caption && (
                  <p className={styles.sectionCaption}>{section.caption}</p>
                )}
                <Box className={styles.grid}>
                  {section.fields.map((field) => (
                    <Box
                      key={field.name}
                      className={[
                        field.half ? styles.half : styles.full,
                        outstanding.has(field.name) ? styles.missing : '',
                      ].join(' ')}
                    >
                      <Field
                        fullWidth
                        select={!!field.options}
                        disabled={!editable}
                        label={field.label}
                        helperText={
                          outstanding.has(field.name)
                            ? 'Required by Twilio'
                            : field.helperText
                        }
                        component={TextField}
                        name={field.name}
                      >
                        {field.options?.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Field>
                    </Box>
                  ))}
                </Box>
              </Box>
            ))}

            {editable && registration?.readyToSubmit && (
              <Box className={`${styles.notice} ${styles.noticeWaiting}`}>
                <Clock size={16} />
                <p className={styles.noticeBody}>
                  Everything carriers need is captured. Submitting sends it for
                  review, which takes a few business days — your details are
                  locked while that happens.
                </p>
              </Box>
            )}

            {editable && (
              <Box className={styles.actions}>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={isSubmitting || !dirty}
                  sx={saveCtaSx}
                >
                  {isSubmitting ? 'Saving…' : 'Save details'}
                </Button>
                {/* Partial saves are allowed, so progress is reported rather
                    than the form refusing to save until complete. */}
                {registration && !registration.readyToSubmit && (
                  <span className={styles.remaining}>
                    {registration.missingDetails.length} field
                    {registration.missingDetails.length === 1 ? '' : 's'} still
                    needed — marked below
                  </span>
                )}
                {registration?.readyToSubmit && !dirty && (
                  <Button
                    type="button"
                    variant="contained"
                    onClick={handleRegister}
                    disabled={submitting}
                    sx={registerCtaSx}
                  >
                    {submitting ? 'Submitting…' : 'Submit for review'}
                  </Button>
                )}
                {registration?.readyToSubmit && dirty && (
                  <span className={styles.ready}>
                    <Check size={14} /> Save your changes, then submit
                  </span>
                )}
              </Box>
            )}
          </Form>
        )}
      </Formik>
    </Box>
  )
}

// sx rather than makeStyles: the theme defaults every Button to contained and
// uppercase, and v4 makeStyles classes lose to MUI v5's emotion styles.
const saveCtaSx = {
  textTransform: 'none',
  fontWeight: 700,
  borderRadius: '8px',
  px: 3,
} as const

// Green rather than the theme primary: submitting is the terminal action on
// this page, and it should not look like the Save button beside it.
const registerCtaSx = {
  ...saveCtaSx,
  backgroundColor: colors.GREEN,
  '&:hover': { backgroundColor: colors.DARK_GREEN },
} as const

const useStyles = makeStyles({
  container: {
    padding: SPACING * 2,
    maxWidth: 720,
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    padding: SPACING * 4,
  },
  // Plain heading elements, not Typography: MUI v5's emotion styles override
  // makeStyles, so colours set here would silently not apply.
  title: {
    margin: 0,
    fontSize: 22,
    fontWeight: 700,
    color: colors.STITCH_BLACK,
  },
  intro: {
    margin: `${SPACING}px 0 ${SPACING * 2}px`,
    fontSize: 14,
    lineHeight: 1.5,
    color: colors.GREY_2,
  },
  notice: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: SPACING,
    padding: SPACING * 1.5,
    borderRadius: 8,
    background: colors.GREY_6,
    marginBottom: SPACING * 2,
  },
  noticeDone: {
    background: colors.PALE_GREEN,
    color: colors.GREEN,
  },
  noticeWaiting: {
    background: colors.PALE_BLUE,
    color: colors.STITCH_BLUE,
  },
  noticeError: {
    background: colors.PALE_RED,
    color: colors.RED,
  },
  noticeTitle: {
    margin: 0,
    fontSize: 14,
    fontWeight: 700,
  },
  noticeBody: {
    margin: 0,
    fontSize: 13,
    lineHeight: 1.5,
  },
  section: {
    marginBottom: SPACING * 3,
  },
  sectionTitle: {
    margin: 0,
    fontSize: 15,
    fontWeight: 700,
    color: colors.STITCH_BLACK,
  },
  sectionCaption: {
    margin: `4px 0 ${SPACING}px`,
    fontSize: 13,
    color: colors.GREY_3,
  },
  grid: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: SPACING * 2,
  },
  full: {
    flex: '1 1 100%',
  },
  half: {
    flex: '1 1 calc(50% - ' + SPACING + 'px)',
    minWidth: 200,
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    gap: SPACING * 2,
  },
  remaining: {
    fontSize: 13,
    color: colors.GREY_3,
  },
  // Plain CSS on the wrapper rather than MUI props: v5's emotion styles
  // override makeStyles on the component itself.
  missing: {
    '& label': { color: colors.ORANGE },
    '& p': { color: colors.ORANGE },
  },
  ready: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    fontSize: 13,
    fontWeight: 600,
    color: colors.GREEN,
  },
})

export default SmsSetup
